import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { MapPin, Users, Clock, DollarSign, Mail, Phone, Globe, Instagram, Volume2 } from 'lucide-react';

const VenueModal = ({ isOpen, onClose, venue, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: [],
    address: '',
    capacity: '',
    hours: '',
    pricing: '',
    soundSystem: '',
    bestFor: '',
    notes: '',
    contact: {
      email: '',
      phone: ''
    },
    website: '',
    social: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const venueTypes = [
    'Nightclub',
    'Rooftop',
    'Beach Club',
    'Industrial',
    'Cultural Hub',
    'After-hours',
    'Restaurant',
    'Bar',
    'Event Space',
    'Open Air'
  ];

  useEffect(() => {
    if (venue) {
      setFormData({
        name: venue.name || '',
        type: venue.type ? (Array.isArray(venue.type) ? venue.type : venue.type.split(', ')) : [],
        address: venue.address || '',
        capacity: venue.capacity || '',
        hours: venue.hours || '',
        pricing: venue.pricing || '',
        soundSystem: venue.soundSystem || '',
        bestFor: venue.bestFor || '',
        notes: venue.notes || '',
        contact: {
          email: venue.contact?.email || '',
          phone: venue.contact?.phone || ''
        },
        website: venue.website || '',
        social: venue.social || ''
      });
    } else {
      // Reset form for new venue
      setFormData({
        name: '',
        type: [],
        address: '',
        capacity: '',
        hours: '',
        pricing: '',
        soundSystem: '',
        bestFor: '',
        notes: '',
        contact: {
          email: '',
          phone: ''
        },
        website: '',
        social: ''
      });
    }
    setErrors({});
  }, [venue]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleTypeToggle = (type) => {
    setFormData(prev => ({
      ...prev,
      type: prev.type.includes(type)
        ? prev.type.filter(t => t !== type)
        : [...prev.type, type]
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (formData.type.length === 0) newErrors.type = 'At least one type is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.capacity.trim()) newErrors.capacity = 'Capacity is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setLoading(true);
    try {
      const venueData = {
        ...formData,
        type: formData.type.join(', ')
      };
      
      await onSave(venueData);
      onClose();
    } catch (error) {
      console.error('Error saving venue:', error);
      setErrors({ submit: error.message || 'Failed to save venue' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={venue ? 'Edit Venue' : 'Add New Venue'}
      maxWidth="max-w-3xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-[#888888] uppercase tracking-wider">Basic Information</h4>
          
          <div>
            <label className="block text-sm font-medium mb-2">
              Venue Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`input-dark w-full ${errors.name ? 'border-red-500' : ''}`}
              placeholder="e.g., LX Factory"
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Venue Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {venueTypes.map(type => (
                <button
                  key={type}
                  type="button"
                  onClick={() => handleTypeToggle(type)}
                  className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                    formData.type.includes(type)
                      ? 'bg-[#D4A574] text-black'
                      : 'bg-[#0A0A0A] hover:bg-[#111111]'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
            {errors.type && <p className="text-red-500 text-sm mt-1">{errors.type}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Address <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              className={`input-dark w-full ${errors.address ? 'border-red-500' : ''}`}
              placeholder="e.g., Rua Rodrigues de Faria 103, 1300-501 Lisboa"
            />
            {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
          </div>
        </div>

        {/* Venue Details */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-[#888888] uppercase tracking-wider">Venue Details</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                <Users size={16} className="inline mr-1" />
                Capacity <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="capacity"
                value={formData.capacity}
                onChange={handleChange}
                className={`input-dark w-full ${errors.capacity ? 'border-red-500' : ''}`}
                placeholder="e.g., 500-1000"
              />
              {errors.capacity && <p className="text-red-500 text-sm mt-1">{errors.capacity}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                <Clock size={16} className="inline mr-1" />
                Operating Hours
              </label>
              <input
                type="text"
                name="hours"
                value={formData.hours}
                onChange={handleChange}
                className="input-dark w-full"
                placeholder="e.g., 23:00-06:00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                <DollarSign size={16} className="inline mr-1" />
                Pricing
              </label>
              <input
                type="text"
                name="pricing"
                value={formData.pricing}
                onChange={handleChange}
                className="input-dark w-full"
                placeholder="e.g., €15-25 entry"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                <Volume2 size={16} className="inline mr-1" />
                Sound System
              </label>
              <input
                type="text"
                name="soundSystem"
                value={formData.soundSystem}
                onChange={handleChange}
                className="input-dark w-full"
                placeholder="e.g., Funktion-One"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Best For</label>
            <input
              type="text"
              name="bestFor"
              value={formData.bestFor}
              onChange={handleChange}
              className="input-dark w-full"
              placeholder="e.g., House, Techno, Live performances"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="input-dark w-full"
              placeholder="Additional information about the venue..."
            />
          </div>
        </div>

        {/* Contact Information */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-[#888888] uppercase tracking-wider">Contact Information</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                <Mail size={16} className="inline mr-1" />
                Email
              </label>
              <input
                type="email"
                name="contact.email"
                value={formData.contact.email}
                onChange={handleChange}
                className="input-dark w-full"
                placeholder="venue@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                <Phone size={16} className="inline mr-1" />
                Phone
              </label>
              <input
                type="tel"
                name="contact.phone"
                value={formData.contact.phone}
                onChange={handleChange}
                className="input-dark w-full"
                placeholder="+351 21 XXX XXXX"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                <Globe size={16} className="inline mr-1" />
                Website
              </label>
              <input
                type="text"
                name="website"
                value={formData.website}
                onChange={handleChange}
                className="input-dark w-full"
                placeholder="www.venue.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                <Instagram size={16} className="inline mr-1" />
                Instagram
              </label>
              <input
                type="text"
                name="social"
                value={formData.social}
                onChange={handleChange}
                className="input-dark w-full"
                placeholder="@venue_instagram"
              />
            </div>
          </div>
        </div>

        {/* Error Message */}
        {errors.submit && (
          <div className="p-4 bg-red-500/10 border border-red-500 rounded-lg">
            <p className="text-red-500 text-sm">{errors.submit}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end pt-4 border-t border-[#333333]">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : venue ? 'Save Changes' : 'Add Venue'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default VenueModal;